import React, { useRef, useState, useMemo, useEffect } from 'react';
import styles from './style.module.css';
import useApi from '../../services/useApi';
import ToggleSwitch from './ToggleSwitch';
import TaskAdd from './TaskAdd';
import GanttChart from './GanttChart';
import preprocessing from './preprocessing';
import Network from './Network';
import RoundGanttChart from './RoundGanttChart';

const API_BASE = '';

async function fetchDevicesByPeriodAPI(startISO, endISO) {
  const url = `${API_BASE}/api/devices?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Devices(by period) fetch failed');
  const data = await res.json();
  return (data || []).map(d => ({
    ...d,
    tasks: (d.tasks || []).map(t => ({ ...t, start: new Date(t.start), end: new Date(t.end) }))
  }));
}

const taskStatuses = ['waiting', 'running', 'completed', 'error', 'timeout'];
const taskStatusColors = {
  waiting: '#9E9E9E',
  running: '#09ff00',
  completed: '#007bff',
  error: '#EF5350',
  timeout: '#FF7043',
};

const now = new Date();
const statuses = ['active', 'success', 'offline'];
const taskNames = ['Init','Job Request','Job Ready','Model Download','Training','Evaluation','Test','Complete','Aggregation Out','Timeout'];

const baseDates = [
  new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1),
  new Date(now.getFullYear(), now.getMonth(), now.getDate()),
  new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1),
];

const randomDate = (minHour = 0, maxHour = 22) => {
  const base = baseDates[Math.floor(Math.random() * baseDates.length)];
  const date = new Date(base);
  date.setHours(minHour + Math.floor(Math.random() * (maxHour - minHour)));
  date.setMinutes(Math.floor(Math.random() * 60));
  return date;
};

const dummyDevices = Array.from({ length: 30 }, (_, i) => {
  const taskCount = 1 + Math.floor(Math.random() * 3);
  const tasks = Array.from({ length: taskCount }, () => {
    const start = randomDate();
    const end = new Date(start.getTime() + 1000 * 60 * (30 + Math.random() * 90));
    const status = taskStatuses[Math.floor(Math.random() * taskStatuses.length)];
    return {
      task: taskNames[Math.floor(Math.random() * taskNames.length)],
      status,
      color: taskStatusColors[status],
      start,
      end,
    };
  });
  return { name: `Device ${i + 1}`, status: statuses[Math.floor(Math.random() * statuses.length)], tasks };
});

const allTasks = dummyDevices.flatMap(d => d.tasks);
const defaultStart = new Date(Math.min(...allTasks.map(t => t.start.getTime())));
const defaultEnd   = new Date(Math.max(...allTasks.map(t => t.end.getTime())));

function toLocalInputValue(dLike) {
  const d = new Date(dLike);
  const tzAdj = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tzAdj.toISOString().slice(0, 16);
}

function isCompleteDateTime(str) {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str);
}

function normalizeRange(startStr, endStr) {
  if (isCompleteDateTime(startStr) && isCompleteDateTime(endStr)) {
    const s = new Date(startStr);
    const e = new Date(endStr);
    if (e <= s) {
      const fixedEnd = new Date(s.getTime() + 60 * 60 * 1000);
      return { startStr, endStr: toLocalInputValue(fixedEnd), ok: true };
    }
    return { startStr, endStr, ok: true };
  }
  return { startStr, endStr, ok: false };
}

function toISOUTC(str) {
  return new Date(str).toISOString();
}

export default function DeviceTimeline() {
  const [isNetwork,setIsNetwork]=useState(false);
  const [roundOpen, setRoundOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const chartRef = useRef(null);
  const [viewMode, setViewMode] = useState('task');

  const [taskId, setTaskId] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  const [deviceData, setDeviceData] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [errorDevices, setErrorDevices] = useState('');

  const handleExport = async () => {
    if (!taskId) return;
    const res = await useApi.get('backboard/timeline/matrix?taskId=' + taskId, {
      responseType: 'blob'
    });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `timeline_${taskId}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const [autoStartISO, autoEndISO] = useMemo(() => {
    const all = deviceData.flatMap(d => d.tasks || []);
    if (!all.length) return [defaultStart.toISOString(), defaultEnd.toISOString()];
    const min = new Date(Math.min(...all.map(t => new Date(t.start).getTime())));
    const max = new Date(Math.max(...all.map(t => new Date(t.end).getTime())));
    return [min.toISOString(), max.toISOString()];
  }, [deviceData]);

  const [customRange, setCustomRange] = useState(null);

  const committedStart = customRange ? customRange.start : new Date(autoStartISO);
  const committedEnd   = customRange ? customRange.end   : new Date(autoEndISO);
  const [startStr, setStartStr] = useState(toLocalInputValue(committedStart));
  const [endStr, setEndStr]     = useState(toLocalInputValue(committedEnd));

  useEffect(()=>{
    if(taskId==='')return;
    useApi.get('backboard/timeline/task?taskId=' + taskId).then((res)=>{
      setDeviceData(preprocessing(res.data))
    }).catch(function (thrown) {
    });
  },[taskId, refreshTick])

  useEffect(() => {
    setStartStr(toLocalInputValue(committedStart));
    setEndStr(toLocalInputValue(committedEnd));
  }, [viewMode, autoStartISO, autoEndISO, customRange]);

  const debounceRef = useRef(0);
  const queueFetchByPeriod = (sStr, eStr) => {
    const { startStr: s2, endStr: e2, ok } = normalizeRange(sStr, eStr);
    setStartStr(s2); setEndStr(e2);
    if (!ok) return;

    const startISO = toISOUTC(s2);
    const endISO = toISOUTC(e2);
    setCustomRange({ start: new Date(startISO), end: new Date(endISO) });

    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setIsLoadingDevices(true);
      setErrorDevices('');
      try {
        const data = await fetchDevicesByPeriodAPI(startISO, endISO);
        setDeviceData(data);
      } catch (e) {
        console.error(e);
        setErrorDevices('기간으로 디바이스 정보를 불러오지 못했어요.');
      } finally {
        setIsLoadingDevices(false);
      }
    }, 200);
  };

  const chartStart = customRange ? customRange.start : new Date(autoStartISO);
  const chartEnd   = customRange ? customRange.end   : new Date(autoEndISO);

  const handleTaskIdChange = (id) => {
    if (id === taskId) setRefreshTick(t => t + 1);
    setTaskId(id);
  };

  const handleRefreshClick = () => {
    if (!taskId) return;
    setRefreshTick(t => t + 1);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.body}>
      <header>
        <span>Device Timeline</span>
        <button
          onClick={handleExport}
          disabled={!taskId || taskId.trim() === ''}
          className={styles.exportBtn}
        >
          Export CSV
        </button>
      </header>
        <div className={styles.toolbar}>
          <ToggleSwitch
            leftText='Task ID'
            rightText='Period'
            checked={viewMode !== 'task'}
            onChange={() => setViewMode(viewMode === 'task' ? 'period' : 'task')}
          />
          {viewMode === 'task' ? (
            <div className={styles.taskRow}>
              <TaskAdd
                onChange={handleTaskIdChange}
                onClose={()=>{}}
              />
              <button type="button" className={styles.refreshBtn} onClick={handleRefreshClick}>Refresh</button>
            </div>
          ) : (
            <div className={`${styles.periodInput} ${styles.flexGrow}`}>
              <input
                type="datetime-local"
                value={startStr}
                onChange={(e) => queueFetchByPeriod(e.target.value, endStr)}
              />
              <input
                type="datetime-local"
                value={endStr}
                onChange={(e) => queueFetchByPeriod(startStr, e.target.value)}
              />
              <div className={styles.periodBtns}>
                <button type="button" onClick={() => queueFetchByPeriod(toLocalInputValue(new Date(new Date(startStr).getTime() - 86400000)), toLocalInputValue(new Date(new Date(endStr).getTime() - 86400000)))} title="-1 day">-1d</button>
                <button type="button" onClick={() => queueFetchByPeriod(toLocalInputValue(new Date(new Date(startStr).getTime() + 86400000)), toLocalInputValue(new Date(new Date(endStr).getTime() + 86400000)))} title="+1 day">+1d</button>
                <button type="button" onClick={() => queueFetchByPeriod(toLocalInputValue(new Date(new Date(startStr).getTime() - 3600000)), toLocalInputValue(new Date(new Date(endStr).getTime() - 3600000)))} title="-1 hour">-1h</button>
                <button type="button" onClick={() => queueFetchByPeriod(toLocalInputValue(new Date(new Date(startStr).getTime() + 3600000)), toLocalInputValue(new Date(new Date(endStr).getTime() + 3600000)))} title="+1 hour">+1h</button>
                <span className={styles.sep} />
                <button type="button" onClick={() => { setCustomRange(null); setStartStr(toLocalInputValue(new Date(autoStartISO))); setEndStr(toLocalInputValue(new Date(autoEndISO))); }} title="데이터 기간으로 리셋">Reset</button>
              </div>
            </div>
          )}
          <div className={styles.dateCtrWrap}>
            <div className={styles.shortcutHelp}>
              <span className={styles.icon}>Shortcut</span>
              <div className={styles.tooltip}>
                🖱️ Alt + Wheel = 확대/축소<br/>
                🖱️ Shift + Wheel = 이동<br/>
                🖱️ 더블클릭 = 확대 초기화<br/>
                🖱️ 드래그 = 구간 확대
              </div>
            </div>
            <div className={styles.dateCtr}>
              <button onClick={() => chartRef.current?.setSpanTo('second')}>Second</button>
              <hr/>
              <button onClick={() => chartRef.current?.setSpanTo('min')}>Min</button>
              <hr/>
              <button onClick={() => chartRef.current?.setSpanTo('hour')}>Hour</button>
              <hr/>
              <button onClick={() => chartRef.current?.setSpanTo('day')}>Day</button>
              <hr/>
              <button type="button" onClick={() => setIsNetwork(!isNetwork)}>3D</button>
            </div>
          </div>
        </div>

        {isLoadingDevices && (<div className={styles.inlineInfo}>데이터 불러오는 중...</div>)}
        {errorDevices && (<div className={styles.inlineError}>{errorDevices}</div>)}

        {!isNetwork?
          <GanttChart
            ref={chartRef}
            devices={deviceData}
            startTime={chartStart}
            endTime={chartEnd}
            onRowClick={(d) => {
              setSelectedDeviceId(d?.id ?? d?.deviceId ?? d?.name ?? '');
              setRoundOpen(true);
            }}
          />
          :
          <Network data={deviceData} />
        }
      </div>
      {roundOpen && (
        <div className={styles.modalBackdrop} onClick={() => setRoundOpen(false)}>
          <div className={styles.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <span>Rounds Timeline</span>
                {selectedDeviceId && (
                  <span className={styles.modalTag}>{selectedDeviceId}</span>
                )}
              </div>
              <button className={styles.modalClose} onClick={() => setRoundOpen(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <RoundGanttChart 
              taskId={taskId}
              deviceId={selectedDeviceId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}